import { ArrowLeft, Save } from 'lucide-react';
import { Controller } from 'react-hook-form';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Alert from '../ui/Alert';
import Input from '../ui/Input';
import FormSection from '../ui/FormSection';
import CEUBasicInfo from './CEUBasicInfo';
import CEUCategorySelection from './CEUCategorySelection';
import CEUProcessingStatus from './CEUProcessingStatus';

export default function CEUFormView({
  control,
  errors,
  handleSubmit,
  isEditing,
  loading,
  error,
  success,
  validationMessages = [],
  userProfile,
  availableCategories = [],
  availableSubcategories = [],
  selectedCategory,
  onBack
}) {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={onBack}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to CEUs
        </Button>
      </div>

      {error && <Alert variant="error" className="mb-4">{error}</Alert>}
      {success && <Alert variant="success" className="mb-4">{success}</Alert>}
      
      {validationMessages.length > 0 && (
        <div className="space-y-2 mb-4">
          {validationMessages.map((msg, index) => (
            <Alert key={index} variant={msg.type}>
              {msg.message}
            </Alert>
          ))}
        </div>
      )}

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <CEUBasicInfo control={control} errors={errors} />
          
          <FormSection
            title="Workshop Description (Optional)"
            description="Copy and paste the official workshop description from course materials if available"
          >
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <div>
                  <textarea
                    {...field}
                    rows={6}
                    placeholder="Paste the workshop description here (optional)..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-error">{errors.description.message}</p>
                  )}
                </div>
              )}
            />
          </FormSection>

          <FormSection
            title="Certificate Upload (Optional)"
            description="Upload your completion certificate for record-keeping and verification"
          >
            <Controller
              name="certificateFile"
              control={control}
              render={({ field: { onChange, value, ...field } }) => (
                <div className="space-y-3">
                  <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <svg className="w-8 h-8 mb-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                        </svg>
                        <p className="mb-2 text-sm text-gray-500">
                          <span className="font-semibold">Click to upload</span> or drag and drop
                        </p>
                        <p className="text-xs text-gray-500">PDF, PNG, JPG or JPEG (MAX. 10MB)</p>
                      </div>
                      <input
                        {...field}
                        type="file"
                        className="hidden"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          onChange(file);
                        }}
                      />
                    </label>
                  </div>
                  
                  {value && (
                    <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        <span className="text-sm font-medium text-green-800">
                          {value.name || 'Certificate uploaded'}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => onChange(null)}
                        className="text-green-600 hover:text-green-800 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                      </button>
                    </div>
                  )}
                  
                  {errors.certificateFile && (
                    <p className="mt-1 text-sm text-error">{errors.certificateFile.message}</p>
                  )}
                </div>
              )}
            />
          </FormSection>

          <CEUCategorySelection
            control={control}
            errors={errors}
            availableCategories={availableCategories}
            availableSubcategories={availableSubcategories}
            selectedCategory={selectedCategory}
            userProfile={userProfile}
          />

          <CEUProcessingStatus control={control} userProfile={userProfile} />

          <div className="flex gap-3 justify-end pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
            >
              <Save className="w-4 h-4 mr-2" />
              {isEditing ? 'Update CEU' : 'Save CEU'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
